/**
 * Draw spot boxes + count banner onto evidence photo for save/history.
 */

export function canvasToBlob(canvas, type = 'image/jpeg', quality = 0.92) {
  return new Promise((resolve) => {
    if (canvas.toBlob) {
      canvas.toBlob((blob) => resolve(blob), type, quality);
      return;
    }
    try {
      const dataUrl = canvas.toDataURL(type, quality);
      fetch(dataUrl)
        .then((r) => r.blob())
        .then(resolve)
        .catch(() => resolve(null));
    } catch (err) {
      resolve(null);
    }
  });
}

function drawCornerBox(ctx, x, y, w, h, color, lineW) {
  const len = Math.max(8, Math.min(w, h) * 0.28);
  ctx.strokeStyle = color;
  ctx.lineWidth = lineW;
  ctx.beginPath();
  // TL
  ctx.moveTo(x, y + len);
  ctx.lineTo(x, y);
  ctx.lineTo(x + len, y);
  // TR
  ctx.moveTo(x + w - len, y);
  ctx.lineTo(x + w, y);
  ctx.lineTo(x + w, y + len);
  // BL
  ctx.moveTo(x, y + h - len);
  ctx.lineTo(x, y + h);
  ctx.lineTo(x + len, y + h);
  // BR
  ctx.moveTo(x + w - len, y + h);
  ctx.lineTo(x + w, y + h);
  ctx.lineTo(x + w, y + h - len);
  ctx.stroke();
}

/**
 * @param {HTMLCanvasElement} sourceCanvas
 * @param {object} opts
 * @returns {Promise<{ canvas: HTMLCanvasElement, blob: Blob, url: string }>}
 */
export async function renderAnnotatedEvidence(sourceCanvas, opts = {}) {
  const {
    overlaySpots = [],
    verdict = 'OK',
    spotCount = overlaySpots.length,
    maxSpotDiameterMm = 5,
  } = opts;

  const w = sourceCanvas.width;
  const h = sourceCanvas.height;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(sourceCanvas, 0, 0);

  const bannerH = Math.max(52, Math.round(h * 0.09));
  ctx.fillStyle = 'rgba(0, 0, 0, 0.72)';
  ctx.fillRect(0, 0, w, bannerH);

  const isNg = verdict === 'NG';
  const title = `พบ ${spotCount} จุด · ${verdict}`;
  ctx.fillStyle = isNg ? '#ff8f8f' : '#7dffab';
  ctx.font = `bold ${Math.max(18, Math.round(w * 0.042))}px sans-serif`;
  ctx.textBaseline = 'middle';
  ctx.fillText(title, Math.round(w * 0.03), bannerH / 2);

  const sub = isNg ? 'ไม่ผ่านเกณฑ์ Appearance' : 'ผ่านเกณฑ์ Appearance';
  ctx.fillStyle = '#fff5f5';
  ctx.font = `${Math.max(12, Math.round(w * 0.024))}px sans-serif`;
  ctx.fillText(sub, Math.round(w * 0.03), bannerH / 2 + Math.round(bannerH * 0.28));

  const lineW = Math.max(2, Math.round(w * 0.0035));
  const fontPx = Math.max(12, Math.round(w * 0.022));

  overlaySpots.forEach((spot, index) => {
    const nx = Number(spot.nx);
    const ny = Number(spot.ny);
    const nw = Number(spot.nw);
    const nh = Number(spot.nh);
    if ([nx, ny, nw, nh].some((v) => Number.isNaN(v))) return;

    const cx = (nx + nw / 2) * w;
    const cy = (ny + nh / 2) * h;
    const side = Math.max(nw * w, nh * h, w * 0.035) * 1.9;
    let x = cx - side / 2;
    let y = cy - side / 2;
    x = Math.max(2, Math.min(w - side - 2, x));
    y = Math.max(bannerH + 2, Math.min(h - side - 2, y));

    const diameterMm = Number(spot.diameterMm) || 0;
    const color = '#ff3b3b';
    drawCornerBox(ctx, x, y, side, side, color, lineW);

    const kind = spot.isKnot || spot.type === 'wood_knot' ? 'ตาไม้' : 'จุดด่างดำ';
    const label = `${index + 1}. ${kind} Ø${diameterMm.toFixed(1)}`;
    ctx.font = `bold ${fontPx}px sans-serif`;
    const tw = ctx.measureText(label).width + 10;
    const th = fontPx + 8;
    let lx = x;
    let ly = y - th - 4;
    if (ly < bannerH + 2) ly = y + side + 4;
    if (lx + tw > w - 2) lx = w - tw - 2;

    ctx.fillStyle = 'rgba(0, 0, 0, 0.78)';
    ctx.fillRect(lx, ly, tw, th);
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    ctx.strokeRect(lx, ly, tw, th);
    ctx.fillStyle = color;
    ctx.textBaseline = 'middle';
    ctx.fillText(label, lx + 5, ly + th / 2);
  });

  // footer count reminder
  const footH = Math.max(36, Math.round(h * 0.055));
  ctx.fillStyle = 'rgba(0, 0, 0, 0.65)';
  ctx.fillRect(0, h - footH, w, footH);
  ctx.fillStyle = '#fff';
  ctx.font = `bold ${Math.max(14, Math.round(w * 0.028))}px sans-serif`;
  ctx.textBaseline = 'middle';
  ctx.fillText(`รวม ${spotCount} จุดบนภาพนี้`, Math.round(w * 0.03), h - footH / 2);

  const blob = await canvasToBlob(canvas);
  if (!blob) {
    throw new Error('สร้างรูปหลักฐานไม่สำเร็จ');
  }
  const url = URL.createObjectURL(blob);
  return { canvas, blob, url };
}

export function downloadBlob(blob, fileName) {
  if (!blob) return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName || `rollscan-${Date.now()}.jpg`;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2500);
}
